import { mdiAutoFix } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import TextField from '@mui/material/TextField'
import { ChatImageBubble, ChatTextBubble } from 'components/Chat'
import { ComposerMediaContext } from 'components/ComposerMediaContext/context'
import { PPDrawer } from 'components/PPDrawer'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { addComposerImages, appendAIChatMessage, upsertAIChat } from 'services/compose'
import { generateCaption } from 'services/compose/suggest/actions'
import { AIConversationMessage } from 'services/compose/suggest/net'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import styles from './AIChatPanel.pcss'

const ICON_SIZE = '20px'

export interface AIChatPanelProps {
  className?: string
  btnClassName?: string
  icon?: React.ReactNode
}

export function AIChatPanel(props: AIChatPanelProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch: composerMediaDispatch } = React.useContext(ComposerMediaContext)

  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [prompt, setPrompt] = React.useState('')
  const [conversationId, setConversationId] = React.useState<string | undefined>(undefined)
  const [messages, setMessages] = React.useState<AIConversationMessage[]>([])
  const messagesEndRef = React.useRef<HTMLDivElement>(null)

  const openDrawer = () => setOpen(true)
  const closeDrawer = () => setOpen(false)

  const onPromptChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPrompt(e.target.value)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !loading) {
      e.preventDefault()
      onSend()
    }
  }

  const onSend = () => {
    if (!prompt.trim() || loading) { return }
    const userMsg: AIConversationMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      kind: 'text',
      text: prompt.trim()
    }
    setMessages(prev => [...prev, userMsg])
    setPrompt('')
    setLoading(true)
    const currentId = conversationId
    if (currentId) {
      dispatch(appendAIChatMessage({ chatId: currentId, message: userMsg }))
    }
    dispatch(generateCaption({ prompt: userMsg.text, conversationId: currentId }))
      .unwrap()
      .then((response: any) => {
        const aiMsg: AIConversationMessage = {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          kind: 'text',
          text: response.text
        }
        setMessages(prev => [...prev, aiMsg])
        const nextId = response.sessionId || currentId
        if (!nextId) { return }
        if (!currentId) {
          dispatch(upsertAIChat({
            id: nextId,
            kind: 'text',
            title: '',
            createdAt: new Date().toISOString(),
            messages: [userMsg, aiMsg]
          }))
          setConversationId(nextId)
        } else {
          dispatch(appendAIChatMessage({ chatId: nextId, message: aiMsg }))
        }
      })
      .finally(() => setLoading(false))
  }

  const onUseImage = (imageUrl: string) => {
    dispatch(addComposerImages({ items: [{ url: imageUrl }], contextDispatch: composerMediaDispatch }))
    dispatch(message('Image added to composer.'))
    closeDrawer()
  }

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const renderTextMessage = (msg: AIConversationMessage & { kind: 'text' }) => (
    <ChatTextBubble key={msg.id} role={msg.role} text={msg.text} />
  )

  const renderImageMessage = (msg: AIConversationMessage & { kind: 'image' }) => (
    <ChatImageBubble key={msg.id} imageUrl={msg.imageUrl} onCreatePost={onUseImage} />
  )

  const messagesBody = (
    <>
      {messages.length === 0 && (
        <div className={styles.empty}>
          <Icon path={mdiAutoFix} size="36px" className={styles['empty-icon']} />
          <span>Describe the caption you need and AI will write it</span>
        </div>
      )}
      {messages.map((msg) => (
        msg.kind === 'image' ? renderImageMessage(msg) : renderTextMessage(msg)
      ))}
      {loading && (
        <div className={styles.typing}>
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </div>
      )}
      <div ref={messagesEndRef} />
    </>
  )

  const renderInputArea = (wrapperClass: string) => (
    <div className={wrapperClass}>
      <TextField
        className={styles.input}
        value={prompt}
        onChange={onPromptChange}
        onKeyDown={onKeyDown}
        multiline
        maxRows={4}
        variant="outlined"
        size="small"
        placeholder="Describe the caption you need..."
        disabled={loading}
      />

      <Button
        className={styles['btn-generate']}
        variant="contained"
        color="primary"
        disabled={!prompt.trim() || loading}
        onClick={onSend}
        startIcon={<Icon path={mdiAutoFix} size={ICON_SIZE} />}
      >
        Generate
      </Button>
    </div>
  )

  return (
    <div className={props.className || ''}>
      <Tooltip title="Write with AI">
        <IconButton size="small" className={props.btnClassName} onClick={openDrawer}>
          {props.icon || <Icon path={mdiAutoFix} size={ICON_SIZE} />}
        </IconButton>
      </Tooltip>
      <PPDrawer
        open={open}
        onClose={closeDrawer}
        title="Write with AI"
        size="md"
        footer={renderInputArea(styles['footer-input'])}
      >
        <div className={styles.messages}>
          {messagesBody}
        </div>
      </PPDrawer>
    </div>
  )
}

export default AIChatPanel
